/**
 * The UI languages. `en` is the source of truth: its dictionaries ship with
 * the code that uses them (route chunks), every other locale is ONE lazy chunk
 * (`locales/<id>/index.ts`) loaded only when that language is picked.
 *
 * Adding a language: add its id here (+ name + Intl tag), create
 * `locales/<id>/` with one file per `en/<ns>.ts` (the compiler and
 * `i18n-dictionaries.spec.ts` list what is missing), and add it to the server
 * presenter (`apps/api/src/modules/notifications/notification-copy.ts`).
 */
export const LOCALES = ['en', 'ru', 'uk', 'es', 'de', 'fr', 'pt-BR'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

/** Each language in its own name — what the picker lists. */
export const LOCALE_NATIVE_NAME: Readonly<Record<Locale, string>> = {
  en: 'English',
  ru: 'Русский',
  uk: 'Українська',
  es: 'Español',
  de: 'Deutsch',
  fr: 'Français',
  'pt-BR': 'Português (Brasil)',
};

/** The BCP-47 tag handed to `Intl.*` (dates, numbers, plural rules). */
export const INTL_TAG: Readonly<Record<Locale, string>> = {
  en: 'en-US',
  ru: 'ru-RU',
  uk: 'uk-UA',
  es: 'es-ES',
  de: 'de-DE',
  fr: 'fr-FR',
  'pt-BR': 'pt-BR',
};

export function isLocale(v: unknown): v is Locale {
  return typeof v === 'string' && (LOCALES as readonly string[]).includes(v);
}

/**
 * The nearest supported locale for a BCP-47 tag, or null: exact id first
 * (`pt-BR`), then the base language (`ru-UA` → ru, `de-AT` → de, `pt-PT` →
 * pt-BR — the only Portuguese we ship). Case-insensitive, `_` tolerated.
 */
export function matchLocale(tag: string | null | undefined): Locale | null {
  if (!tag) return null;
  const norm = tag.trim().replace(/_/g, '-').toLowerCase();
  if (!norm) return null;
  const exact = LOCALES.find((l) => l.toLowerCase() === norm);
  if (exact) return exact;
  const base = norm.split('-')[0];
  if (base === 'pt') return 'pt-BR';
  return LOCALES.find((l) => l === base) ?? null;
}

/** The first browser language we support, else English. */
export function detectBrowserLocale(
  languages: readonly string[] | undefined = typeof navigator === 'undefined'
    ? undefined
    : navigator.languages?.length
      ? navigator.languages
      : [navigator.language],
): Locale {
  for (const tag of languages ?? []) {
    const hit = matchLocale(tag);
    if (hit) return hit;
  }
  return DEFAULT_LOCALE;
}
